import gql from 'graphql-tag';
import { FC, useState } from 'react';
import { useMutation } from '@apollo/client';
import { useNotify, useRedirect } from 'react-admin';

import client from 'src/data/api';
import CraftCreate from 'src/components/CraftCreate';
import CraftTabbedForm from 'src/components/CraftTabbedForm';
import DetailsTab from './formTabs/Details';
import LoadingOverlay from 'src/components/LoadingOverlay';
import { createIdentityExternalClientSchema } from './validation';
import { zodIssuesIntoErrorsMap } from 'src/utils/validation';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';

const SecretRevealDialog: FC<{
  clientId: string | null;
  secret: string | null;
  onClose: () => void;
}> = ({ clientId, secret, onClose }) => {
  const notify = useNotify();
  const [showSecret, setShowSecret] = useState(false);

  const handleCopyClientId = () => {
    if (clientId) {
      navigator.clipboard.writeText(clientId);
      notify('Client ID copied to clipboard', { type: 'success' });
    }
  };

  const handleCopySecret = () => {
    if (secret) {
      navigator.clipboard.writeText(secret);
      notify('Secret copied to clipboard', { type: 'success' });
    }
  };

  const handleClose = () => {
    setShowSecret(false);
    onClose();
  };

  return (
    <Dialog open={!!secret} maxWidth="sm" fullWidth>
      <DialogTitle>Client Credentials</DialogTitle>
      <DialogContent>
        <Typography sx={{ mb: 2 }}>
          The client was successfully created. Please copy its credentials below, and store them in a safe place before
          closing this dialog.
        </Typography>
        <Alert severity="warning" sx={{ mb: 2 }}>
          This is the only time the client secret will be viewable.
        </Alert>
        <TextField
          label="Client ID"
          value={clientId ?? ''}
          fullWidth
          sx={{ mb: 2 }}
          slotProps={{
            input: {
              readOnly: true,
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={handleCopyClientId} edge="end">
                    <ContentCopyIcon />
                  </IconButton>
                </InputAdornment>
              ),
            },
          }}
        />
        <TextField
          label="Secret"
          value={secret ?? ''}
          type={showSecret ? 'text' : 'password'}
          fullWidth
          slotProps={{
            input: {
              readOnly: true,
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={() => setShowSecret((prev) => !prev)} edge="end" sx={{ mr: -0.5 }}>
                    {showSecret ? <VisibilityOffIcon /> : <VisibilityIcon />}
                  </IconButton>
                  <IconButton onClick={handleCopySecret} edge="end">
                    <ContentCopyIcon />
                  </IconButton>
                </InputAdornment>
              ),
            },
          }}
        />
      </DialogContent>
      <DialogActions sx={{ justifyContent: 'center' }}>
        <Button variant="contained" onClick={handleClose}>
          Done
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export const ExternalClientsCreate: FC = () => {
  const notify = useNotify();
  const redirect = useRedirect();
  const [secretPlainText, setSecretPlainText] = useState<string | null>(null);
  const [createdClientId, setCreatedClientId] = useState<string | null>(null);

  const [createExternalClient, { loading }] = useMutation<{
    createIdentityExternalClient: { id: string; secretPlainText: string };
  }>(
    gql`
      mutation createIdentityExternalClient($externalClient: CreateIdentityExternalClientInput!) {
        createIdentityExternalClient(externalClient: $externalClient) {
          id
          secretPlainText
        }
      }
    `,
    {
      client,
      errorPolicy: 'all',
    },
  );

  const handleSave = async (rawValues: Record<string, unknown>) => {
    const validationResult = createIdentityExternalClientSchema.safeParse(rawValues);
    if (!validationResult.success) return zodIssuesIntoErrorsMap(validationResult.error.issues);
    const values = validationResult.data;

    const { data, errors } = await createExternalClient({
      variables: {
        externalClient: {
          name: values.name,
          description: values.description,
          organizationId: values.org_id,
          legacyClientId: values.legacy_client_id,
        },
      },
    });

    if (errors) {
      notify(errors[0].message, { type: 'error' });
      return undefined;
    }

    const result = data?.createIdentityExternalClient;
    if (!result?.id) {
      notify('Unable to redirect to new external client: ID not returned', { type: 'error' });
      return redirect('list', 'identity_external_clients');
    }

    setCreatedClientId(result.id);
    setSecretPlainText(result.secretPlainText);
    notify('The external client has been successfully created', { type: 'success' });
    return undefined;
  };

  const handleCloseSecretDialog = () => {
    setSecretPlainText(null);
    redirect('edit', 'identity_external_clients', createdClientId ?? '');
  };

  return (
    <CraftCreate header="Create External Client">
      <Alert severity="warning">
        IMPORTANT: Please consult with DevSecOps in the{' '}
        <Typography component="span" fontWeight="900">
          #dev-sec-ops
        </Typography>{' '}
        Slack channel to provision clients for customers.
      </Alert>
      <CraftTabbedForm formType="create" onSubmit={handleSave} shouldUnregister>
        <DetailsTab mode="create" />
      </CraftTabbedForm>

      <SecretRevealDialog clientId={createdClientId} secret={secretPlainText} onClose={handleCloseSecretDialog} />

      <LoadingOverlay open={loading} />
    </CraftCreate>
  );
};

export default ExternalClientsCreate;
